import { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import TemplatesTableFilters from './components/TemplatesTableFilters/TemplatesTableFilters';
import TemplatesTable from './components/TemplatesTable/TemplatesTable';
import { ReactComponent as PlusIcon } from 'assets/plus-without-circle.svg';
import Loader from 'components/common/Loader';
//eslint-disable-next-line max-len
import i18nContext from 'components/i18n-context';
import { filteringPaymentMethods } from 'components/common/PaymentForm/utils';
import Pagination from 'components/common/Pagination/Pagination';
//eslint-disable-next-line max-len
import PopUpDeleteTransactionConfirmationScheme from 'components/common/PopUpScheme/PopUpDeleteTransactionConfirmationScheme';
import PopUpUseTemplateScheme from 'components/common/PopUpScheme/PopUpUseTemplateScheme';
import UpdateTemplateModel from 'components/Templates/models/UpdateTemplateModel';
import UseTemplateModel from 'components/Templates/models/UseTemplateModel';
import { ROUTE_PATHS } from 'routes/constants';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { PopUp } from 'uikit/PopUp/PopUp';
import './TemplatesTablePage.scss';
import { PAYMENT_METHOD, PAYMENT_PROVIDERS } from '../../../common/constants';

const popUpInitialData = {
  isShowPopUp: false,
  popUpType: '',
  title: '',
  description: '',
  confirmButtonLabel: '',
  cancelButtonLabel: '',
  templateData: {},
  onCancel: () => {},
  onConfirm: () => {}
};

const POP_UP_TYPE = {
  USE_TEMPLATE: 'USE_TEMPLATE',
  DELETE: 'DELETE'
};

const TemplatesTablePage = ({ customerNumber, userAccounts, paymentStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  useEffect(() => {
    paymentStore.resetTemplateCreationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (customerNumber) {
      paymentStore.getTemplatesList(customerNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerNumber,
    paymentStore.templatesFilters.currency,
    paymentStore.templatesFilters.search_text,
    paymentStore.templatesPagination.size,
    paymentStore.templatesPagination.page
  ]);

  useEffect(() => {
    if (customerNumber && paymentStore.isTemplateSuccessfullyDeleted) {
      paymentStore.getTemplatesList(customerNumber);

      paymentStore.setIsTemplateSuccessfullyDeleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, paymentStore.isTemplateSuccessfullyDeleted]);

  const handleEdit = async (templateDataForUpdate) => {
    await paymentStore.setTemplateData(UpdateTemplateModel(templateDataForUpdate));
    await paymentStore.setEditTemplateId(paymentStore.templateData.templateId);
    navigate(ROUTE_PATHS.TEMPLATES_CREATE);
  };

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  const handleConfirmTemplateDeletion = async (templateId) => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.DELETE,
      title: i18n.getMessage('templates.popUp.deleteTemplate.title'),
      description: i18n.getMessage('templates.popUp.deleteTemplate.description'),
      confirmButtonLabel: i18n.getMessage('templates.popUp.deleteTemplate.confirm'),
      cancelButtonLabel: i18n.getMessage('templates.popUp.deleteTemplate.cancel'),
      onConfirm: async () => {
        await paymentStore.deleteTemplateRequest(customerNumber, templateId);

        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  const handleUseTemplate = async (templateData) => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.USE_TEMPLATE,
      title: i18n.getMessage('templates.popUp.useTemplate.title'),
      confirmButtonLabel: i18n.getMessage('templates.popUp.useTemplate.confirm'),
      cancelButtonLabel: i18n.getMessage('templates.popUp.useTemplate.cancel'),
      templateData: templateData,
      onConfirm: async (walletNumber) => {
        const senderAccount = userAccounts.find((userAccount) => userAccount.wallet_number === walletNumber);
        const isCryptoCurrency = !!templateData?.requisites?.cryptoAddress;

        paymentStore.setSelectedAccount(senderAccount);
        paymentStore.setPreviousTransactionInfo(UseTemplateModel(templateData), false, isCryptoCurrency);
        paymentStore.setIsRepeatPaymentStatus(true);
        paymentStore.setPaymentMethod(
          senderAccount?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
            ? PAYMENT_METHOD.CRYPTO
            : templateData.paymentMethod
        );
        paymentStore.setAvailablePaymentMethods(filteringPaymentMethods(senderAccount?.payment_methods));
        paymentStore.setIsTemplateTransaction(true);

        navigate(ROUTE_PATHS.NEW_PAYMENT);

        resetPopUpData();
      },
      onCancel: () => {
        paymentStore.setSelectedAccount(null);
        paymentStore.setError(null);
        resetPopUpData();
      }
    });
  };

  const handleCreateTemplateButton = () => {
    paymentStore.setEditTemplateId(null);
    navigate(ROUTE_PATHS.TEMPLATES_CREATE);
  };

  const handleSetCurrentPage = async (pageNumber) => {
    paymentStore.setTemplatesPage(pageNumber);
  };

  const handleSetPageSize = (size) => {
    paymentStore.setTemplatesPageSize(size);
  };

  return (
    <NewContainer
      className={'templates-page content-card-wrapper'}
      header={i18n.getMessage('container.savedTemplates')}
    >
      {!paymentStore.isTemplatesListFetched ? (
        <div className={'templates-loader'}>
          <Loader />
        </div>
      ) : (
        <>
          <div className={'templates-actions'}>
            <button className={'create-template-button'} onClick={handleCreateTemplateButton}>
              {i18n.getMessage('templates.button.createTemplate')}
              <PlusIcon className={'create-template-plus-icon'} />
            </button>
          </div>
          <TemplatesTableFilters
            filters={paymentStore.templatesFilters}
            userAccounts={userAccounts}
            onFilterChange={paymentStore.setTemplatesFilter}
            handleSearchText={({ target: { name, value } }) => {
              paymentStore.setTemplatesFilter(name, value);
              paymentStore.getTemplatesListWithDebounce(customerNumber);
            }}
          />
          {paymentStore.templatesList?.length === 0 ? (
            <div className={'empty-templates-wrapper'}>
              <h2 className={'empty-templates-header'}>{i18n.getMessage('templates.emptyTemplates.header')}</h2>
              <p className={'empty-templates-text'}>{i18n.getMessage('templates.emptyTemplates.description')}</p>
            </div>
          ) : (
            <>
              <TemplatesTable
                templates={paymentStore.templatesList}
                onUse={handleUseTemplate}
                onRemove={handleConfirmTemplateDeletion}
                onEdit={handleEdit}
                isLoading={paymentStore.isLoading}
                userAccounts={userAccounts}
              />
              <Pagination
                isShowPaginationSize={true}
                paginationSize={paymentStore.templatesPagination.size}
                handleChangePaginationSize={handleSetPageSize}
                isLoading={paymentStore.isLoading}
                currentPage={paymentStore.templatesPagination.page}
                numberOfPages={paymentStore.templatesTotalPages}
                setCurrentPage={handleSetCurrentPage}
              />
            </>
          )}
        </>
      )}

      <PopUp className={'templates-popup'} show={popUpData.isShowPopUp} onClose={popUpData.onCancel}>
        <>
          {popUpData.popUpType === POP_UP_TYPE.DELETE && (
            <PopUpDeleteTransactionConfirmationScheme
              title={popUpData.title}
              description={popUpData.description}
              closeButtonLabel={popUpData.cancelButtonLabel}
              confirmButtonLabel={popUpData.confirmButtonLabel}
              onClose={popUpData.onCancel}
              onConfirm={popUpData.onConfirm}
            />
          )}
          {popUpData.popUpType === POP_UP_TYPE.USE_TEMPLATE && (
            <PopUpUseTemplateScheme
              title={popUpData.title}
              description={popUpData.description}
              closeButtonLabel={popUpData.cancelButtonLabel}
              confirmButtonLabel={popUpData.confirmButtonLabel}
              onClose={popUpData.onCancel}
              onConfirm={popUpData.onConfirm}
              isLoading={paymentStore.isLoading}
              templateData={popUpData.templateData}
              userAccounts={userAccounts}
              customerNumber={customerNumber}
            />
          )}
        </>
      </PopUp>
    </NewContainer>
  );
};

TemplatesTablePage.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore.userAccounts,
  paymentStore: stores.paymentStore
}))(observer(TemplatesTablePage));
